/* Lightweight functional programming utilities */

/**
 * Creates a function that negates the result of another function.
 *
 * The returned function calls the original function with the same arguments
 * and returns the boolean negation of its result.
 *
 * @param {Function} fn
 * The function whose result will be negated.
 *
 * @returns {Function}
 * A new function that returns `true` when `fn` returns a falsy value,
 * and `false` when `fn` returns a truthy value.
 *
 * @example
 * const isNil = (v) => v === null;
 * const isNotNil = complement(isNil);
 *
 * isNil(null);     // => true
 * isNotNil(null);  // => false
 *
 * isNil(7);        // => false
 * isNotNil(7);     // => true
 */
const complement =
  (fn) =>
  (...args) =>
    !fn.apply(this, args);

/**
 * Creates a function which is a composition of the passed functions. Each
 * function consumes the return value of the function that follows.
 * Composing functions `f()`, `g()`, and `h()` would produce the result
 * of `f(g(h()))`.
 *
 * @name compose
 * @param  {...Function} fns Functions to compose
 * @returns {Function} Function that is the composed `functions`
 * @example
 * const add1 = (n) => n + 1;
 * const mul2 = (n) => n * 2;
 *
 * const add1AndMul2 = compose(mul2, add1);
 * add1AndMul2(4) // = 10
 *
 * const mul2AndAdd1 = compose(add1, mul2);
 * mul2AndAdd1(4) // = 9
 */
const compose =
  (...fns) =>
  (x) =>
    fns.reduceRight((v, f) => f(v), x);

/**
 * Creates a function which is a composition of the passed asynchronous
 * functions. Each function consumes the value of the promise that returns the
 * function that follows. Composing functions `f()`, `g()`, and `h()` would
 * produce the result of `h.then(g).then(f)`.
 *
 * @name composeAsync
 * @param  {...Function} fns Functions to compose
 * @returns {Function} Function that is the composed `functions`
 * @example
 * const add1 = (n) => n + 1;
 * const mul2 = (n) => n * 2;
 *
 * const add1AndMul2 = compose(mul2, add1);
 * add1AndMul2(4).then(c => c) // c = 10
 */
const composeAsync =
  (...fns) =>
  (x) =>
    fns.reduceRight((v, f) => v.then(f), Promise.resolve(x));

/**
 * Partially applies a function that expects exactly two arguments.
 *
 * This helper allows a function with two parameters to be called in
 * two different ways:
 *
 * 1) All at once:
 *    fn(a, b)
 *
 * 2) In two steps:
 *    fn(a)(b)
 *
 * This is useful when you want to configure a function first
 * (by providing the first argument) and apply it later with
 * the second argument.
 *
 * Important notes:
 * - This helper is intentionally limited to functions with TWO arguments.
 * - It does NOT support arbitrary numbers of arguments.
 * - It avoids complexity and hidden behavior on purpose.
 *
 * Example:
 *
 *   function add(a, b) {
 *     return a + b;
 *   }
 *
 *   const addCurried = curry2(add);
 *
 *   addCurried(2, 3); // → 5
 *   addCurried(2)(3); // → 5
 *
 * @param {Function} fn
 * A function that expects exactly two arguments.
 *
 * @returns {Function}
 * A new function that can be called either with both arguments at once,
 * or with the first argument and later with the second one.
 */
function curry2(fn) {
  return function curried(a, b) {
    // If both arguments are provided, call the original function immediately
    if (arguments.length === 2) {
      return fn(a, b);
    }

    // Otherwise, return a function that expects the second argument
    return function applySecondArgument(bb) {
      return fn(a, bb);
    };
  };
}

/**
 * A function that does nothing but return the parameter supplied to it. Good
 * as a default or placeholder function.
 * @param {any} x Any value
 * @returns {any} Same x
 */
const identity = (x) => x;

/**
 * Creates a function which a composition of the passed functions, but in a
 * reverse order than `compose()`. The params can be read as a sequence of
 * events.
 *
 * @name pipe
 * @param  {...Function} fns Functions as a sequence
 * @returns {Function} Function that is the composed `functions`
 */
const pipe =
  (...fns) =>
  (x) =>
    fns.reduce((v, f) => f(v), x);

/**
 * Creates a function which a composition of the passed asynchronous functions,
 * but in a reverse order than `composeAsync()`.
 *
 * @name pipeAsync
 * @param  {...Function} fns Functions as a sequence
 * @returns {Function} Function that is the composed `functions`
 */
const pipeAsync =
  (...fns) =>
  (x) =>
    fns.reduce((v, f) => v.then(f), Promise.resolve(x));

/**
 * Memoizes an async function by caching the returned Promise.
 * Concurrent calls before the first resolution share the same Promise.
 * On rejection, the cache is cleared so the next call retries.
 *
 * Works reliably when `fn` receives primitives or POJOs as arguments.
 * Argument types that may cause collisions or unexpected cache hits:
 * functions, Symbols, undefined, circular references, and class instances
 * (two distinct instances with identical properties are treated as equal).
 *
 * @param {Function} fn - Async function to memoize.
 * @returns {Function} Memoized version of `fn`.
 * @example
 * const fetchOnce = memoizeAsync(fetchExpensiveData);
 * fetchOnce(); // makes the network call
 * fetchOnce(); // returns the same Promise, no second call
 */
function memoizeAsync(fn) {
  const cache = new Map();
  return function memoized(...args) {
    const key = JSON.stringify(args);
    if (!cache.has(key)) {
      cache.set(
        key,
        Promise.resolve(fn(...args)).catch((err) => {
          cache.delete(key);
          throw err;
        })
      );
    }
    return cache.get(key);
  };
}

module.exports = {
  complement,
  compose,
  composeAsync,
  curry2,
  identity,
  memoizeAsync,
  pipe,
  pipeAsync,
};
